import { z } from "zod/v4";

/**
 * How a recommendation reads, written by the composer after Rex has picked.
 *
 * The card's decisions stay the record the build reads. This is the one place
 * that decides how the merchant meets them, so changing how recommendations
 * read changes this schema and the composer's prompt, and nothing else.
 */

/** One statement, and the facts it rests on. */
export const AuditPresentedFactSchema = z.object({
  text: z.string().describe("One complete sentence in plain words."),
  cites: z
    .array(z.string())
    .describe(
      "The ids of the facts this sentence rests on, exactly as given: an observation id like O2, a decision's node like auditShippingValue, or length. Empty only when the sentence states no number and nothing about the store."
    ),
});

export const AuditRecommendationPresentationSchema = z.object({
  title: z
    .string()
    .describe(
      "<Plain page or area name>: <the change, starting with a verb>. Under 60 characters, sentence case."
    ),
  description: z
    .string()
    .describe(
      "Two sentences, at most 50 words. What changes, with the before and after. Then why it should earn more, as a mechanism a shopper would recognize."
    ),
  chips: z
    .array(
      z.object({
        label: z.string(),
        warn: z
          .boolean()
          .describe("Something the merchant should weigh before starting."),
      })
    )
    .describe("Run time, test shape, then the metric it is judged on."),
  test: z.object({
    where: z.string().describe("The page or area, as the merchant names it."),
    place: z
      .string()
      .nullable()
      .describe(
        "Where on the page a shopper would point, named by what it shows. Null when the test is not on one place."
      ),
    today: z
      .string()
      .describe(
        "What the shopper sees now, quoting real text or values. When it could not be read, say plainly what could not be read and why."
      ),
    proposed: z
      .array(z.string())
      .min(1)
      .describe(
        "What the shopper would see instead, concretely. One line per version when there is more than one."
      ),
    built: z.string().describe("How it is built, in plain words."),
  }),
  why: z
    .array(AuditPresentedFactSchema)
    .describe(
      "Two to four reasons this should work on this store, biggest first."
    ),
  tradeoff: AuditPresentedFactSchema.nullable().describe(
    "The main tradeoff, or null when there is none."
  ),
  saw: z
    .array(AuditPresentedFactSchema)
    .describe(
      "Three to five facts behind the reasons, with numbers and a time window."
    ),
  worthKnowing: z
    .array(AuditPresentedFactSchema)
    .describe(
      "Up to two things that could change the decision or the result. Empty when there is nothing."
    ),
  decided: z
    .array(AuditPresentedFactSchema)
    .describe("Two to four lines on how this test was chosen."),
});

export type AuditPresentedFact = z.infer<typeof AuditPresentedFactSchema>;
export type AuditRecommendationPresentation = z.infer<
  typeof AuditRecommendationPresentationSchema
>;
